import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { resolveServiceName } from './error-messages';
import { ErrorDetailsExpander } from '../notifications/ErrorDetailsExpander';
import { getSDK } from '../../services/sdk-instance';
import type { ErrorEntry, ErrorSeverity } from '../../types/error-events-types';
import {
  visibleErrorEntriesAtom,
  unreadErrorCountAtom,
  markAllReadAction,
  clearErrorsAction,
  toggleErrorReadAction,
  setErrorEntriesFromListAction,
} from '../../stores/errors/atoms';

const SEVERITIES: ErrorSeverity[] = ['warning', 'error', 'critical'];

export function LiveErrorList() {
  const navigate = useNavigate();
  const errorEntries = useAtomValue(visibleErrorEntriesAtom);
  const unreadErrors = useAtomValue(unreadErrorCountAtom);
  const markAllErrorsRead = useSetAtom(markAllReadAction);
  const clearErrors = useSetAtom(clearErrorsAction);
  const toggleErrorRead = useSetAtom(toggleErrorReadAction);
  const setErrorsFromList = useSetAtom(setErrorEntriesFromListAction);
  const [filterOrigin, setFilterOrigin] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [retrying, setRetrying] = useState(false);

  const origins = useMemo(
    () => Array.from(new Set(errorEntries.map((e) => e.service_origin))).sort(),
    [errorEntries],
  );

  const filteredErrors = useMemo(
    () =>
      errorEntries.filter(
        (e) =>
          (filterOrigin === 'all' || e.service_origin === filterOrigin) &&
          (filterSeverity === 'all' || e.severity === filterSeverity),
      ),
    [errorEntries, filterOrigin, filterSeverity],
  );

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const res = await getSDK().errors.listErrors({ limit: 100, apply_mute: true });
      setErrorsFromList(res.errors);
    } catch (error) {
      console.error('Failed to refresh errors:', error);
    } finally {
      setRetrying(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await getSDK().errors.markRead({ all: true, read: true });
      markAllErrorsRead();
    } catch (error) {
      console.error('Failed to mark all errors read:', error);
    }
  };

  const handleClearAll = async () => {
    try {
      await getSDK().errors.dismiss({ all: true });
      clearErrors();
    } catch (error) {
      console.error('Failed to clear errors:', error);
    }
  };

  const handleToggleRead = async (entry: ErrorEntry) => {
    try {
      await getSDK().errors.markRead({ event_ids: entry.eventIds, read: !entry.read });
      toggleErrorRead(entry.id);
    } catch (error) {
      console.error('Failed to update read state:', error);
    }
  };

  return (
    <section id="errors" data-testid="settings-errors" className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Errors</h2>
        <p className="text-sm text-muted-foreground">
          Live error notifications for this account
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <select
          data-testid="errors-filter-origin"
          value={filterOrigin}
          onChange={(e) => setFilterOrigin(e.target.value)}
          className="rounded bg-muted px-2 py-1 text-xs"
          aria-label="Filter by source"
        >
          <option value="all">All sources</option>
          {origins.map((o) => (
            <option key={o} value={o}>
              {resolveServiceName(o)}
            </option>
          ))}
        </select>
        <select
          data-testid="errors-filter-severity"
          value={filterSeverity}
          onChange={(e) => setFilterSeverity(e.target.value)}
          className="rounded bg-muted px-2 py-1 text-xs"
          aria-label="Filter by severity"
        >
          <option value="all">All severities</option>
          {SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <div className="ml-auto flex items-center gap-3">
          <button
            type="button"
            data-testid="errors-retry"
            onClick={() => {
              void handleRetry();
            }}
            disabled={retrying}
            className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-40"
          >
            {retrying ? 'Retrying…' : 'Retry'}
          </button>
          <button
            type="button"
            data-testid="errors-mark-all-read"
            onClick={() => {
              void handleMarkAllRead();
            }}
            disabled={unreadErrors === 0}
            className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-40"
          >
            Mark all read
          </button>
          <button
            type="button"
            data-testid="errors-clear-all"
            onClick={() => {
              void handleClearAll();
            }}
            disabled={errorEntries.length === 0}
            className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-40"
          >
            Clear all
          </button>
        </div>
      </div>
      {filteredErrors.length === 0 ? (
        <div
          data-testid="errors-empty"
          className="flex h-20 items-center justify-center rounded-lg text-sm text-muted-foreground"
        >
          {errorEntries.length === 0 ? 'No errors' : 'No errors match the current filter'}
        </div>
      ) : (
        <div className="rounded-lg">
          {filteredErrors.map((entry) => (
            <ErrorDetailsExpander
              key={entry.id}
              entry={entry}
              onNavigateSession={(sessionId) => {
                navigate(`/chat/${sessionId}`);
              }}
              onToggleRead={(e) => {
                void handleToggleRead(e);
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}
